import { useContext, useState, useMemo } from "react";
import dayjs from "dayjs";
import localeData from "dayjs/plugin/localeData";
import { AppContext } from "../../root/Root";
import { DefaultLayout } from "../../../layouts/DefaultLayout";
import Grid from "@mui/material/Unstable_Grid2";

import {
  Stack,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Button,
  Paper,
  MenuItem,
  Checkbox,
  ListItemText,
  Box,
  Typography,
} from "@mui/material";

import {
  SendOutlined,
  DescriptionOutlined,
  FileDownloadOutlined,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";

import { SplitButton } from "../../../components/common/splitButton/SplitButton";
import { SpendsTotalCards } from "../../../components/eRetail/spendsTotalCards/SpendsTotalCards";
import { SpendsRefreshButton } from "../../../components/eRetail/spendsRefreshButton/SpendsRefreshButton";

import { useSpends } from "../../../hooks/eRetail/useSpends";

import { spendsStrings } from "./locale";

import { downloadTextFile, initLocalization } from "../../../utils/helpers";

import {
  DEFAULT_E_RETAIL_SPENDS_COMPUTE_OPTIONS,
  E_RETAIL_SPEND_MESSAGE_TYPES,
  E_RETAIL_SPEND_STATUSES,
  E_RETAIL_SPEND_STATUS_TYPES,
} from "../../../constants";

import "../../../style/common.scss";

dayjs.extend(localeData);

const currentYear = dayjs().year();

export const Spends = () => {
  const { language } = useContext(AppContext);

  initLocalization(spendsStrings);

  const [year, setYear] = useState(currentYear);
  const [selectedMonthId, setSelectedMonthId] = useState(null);
  const [computeOptions, setComputeOptions] = useState(
    DEFAULT_E_RETAIL_SPENDS_COMPUTE_OPTIONS,
  );
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    onConfirm: () => {},
  });

  const { spends, getSpends, computeSpends, transferSpends, downloadSpends } =
    useSpends(true);

  const monthsList = useMemo(
    () => dayjs().locale(language).localeData().monthsShort(),
    [language],
  );
  const selectedMonth = useMemo(
    () => spends.find((spend) => spend.month === selectedMonthId) || {},
    [selectedMonthId, spends],
  );
  const progressMessages = useMemo(
    () =>
      [...(selectedMonth?.spendProgressMessages || [])].sort(
        (a, b) => new Date(b.addedDate) - new Date(a.addedDate),
      ),
    [selectedMonth],
  );
  const totals = useMemo(
    () => ({
      finished: spends.filter(
        (spend) => spend.spendStatusId === E_RETAIL_SPEND_STATUS_TYPES.finished,
      ).length,
      inProgress: spends.filter(
        (spend) =>
          spend.spendStatusId === E_RETAIL_SPEND_STATUS_TYPES.inProgress,
      ).length,
      error: spends.filter(
        (spend) => spend.spendStatusId === E_RETAIL_SPEND_STATUS_TYPES.error,
      ).length,
    }),
    [spends],
  );

  const getSpendStatus = (index) => {
    const spend = spends.find((spend) => spend.month === index + 1);
    return E_RETAIL_SPEND_STATUSES[spend?.spendStatusId];
  };

  const buildSpendsPayload = (monthId, newYear) => ({
    includeMetadata: false,
    yearMonths: monthsList.map((month, index) => ({
      year: newYear || year,
      month: index + 1,
      includeProcessMessages: index + 1 === monthId,
    })),
  });

  const buttonMenu = ["reloadData", "includeExtraData"];

  const handleButtonMenuClick = (event, index) => {
    setComputeOptions({
      ...computeOptions,
      [buttonMenu[index]]: !computeOptions[buttonMenu[index]],
    });
  };

  const buildButtonMenu = () => {
    return buttonMenu.map((option, index) => (
      <MenuItem
        key={option}
        onClick={(event) => handleButtonMenuClick(event, index)}
      >
        <Checkbox checked={computeOptions[option]} />
        <ListItemText primary={spendsStrings[option]} />
      </MenuItem>
    ));
  };

  const disableButtons = () => {
    return (
      !selectedMonthId ||
      selectedMonth?.spendStatusId === E_RETAIL_SPEND_STATUS_TYPES.inProgress
    );
  };

  const disableTransfer = () => {
    return (
      selectedMonth?.spendStatusId !== E_RETAIL_SPEND_STATUS_TYPES.finished
    );
  };

  const handleRefresh = () => {
    getSpends(buildSpendsPayload(selectedMonthId));
  };

  const handleChangeYear = (val) => {
    const newYear = year + val;
    setYear(newYear);
    setSelectedMonthId(null);
    getSpends(buildSpendsPayload(null, newYear));
  };

  const handleSelectMonth = async (monthId) => {
    await getSpends(buildSpendsPayload(monthId));

    setSelectedMonthId(monthId);
    setComputeOptions(DEFAULT_E_RETAIL_SPENDS_COMPUTE_OPTIONS);
  };

  const handleDownload = () => {
    downloadSpends({
      fileName: selectedMonth.fileName,
    }).then((data) => {
      downloadTextFile(selectedMonth.fileName, data);
    });
  };

  const handleCompute = () => {
    if (!selectedMonthId) {
      return;
    }

    if (selectedMonth.numberOfComputes) {
      setDialogConfig({
        isOpen: true,
        onConfirm: async () => {
          await computeSpends({
            year,
            month: selectedMonthId,
            ...computeOptions,
          });
          setDialogConfig((prev) => ({ ...prev, isOpen: false }));
        },
      });
      return;
    }

    computeSpends({
      year,
      month: selectedMonthId,
      ...computeOptions,
    });
  };

  const handleTransfer = () => {
    if (!selectedMonthId) {
      return;
    }

    if (selectedMonth.numberOfComputes) {
      setDialogConfig({
        isOpen: true,
        onConfirm: async () => {
          await transferSpends({
            year,
            month: selectedMonthId,
          });
          setDialogConfig((prev) => ({ ...prev, isOpen: false }));
        },
      });
      return;
    }

    transferSpends({
      year,
      month: selectedMonthId,
    });
  };

  const handleCloseDialog = () => {
    setDialogConfig((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <DefaultLayout>
      <Grid
        className="page-content"
        container
        rowSpacing={{ xs: 3, md: 0 }}
        columnSpacing={{ xs: 0, md: 2 }}
        padding={{ xs: 1, md: 2 }}
        disableEqualOverflow
      >
        <Grid xs={12}>
          <Grid container direction="column" height="100%">
            <Grid xs={12}>
              <Paper sx={{ padding: "0.5rem", position: "relative" }}>
                <Grid
                  xs={12}
                  container
                  justifyContent="space-between"
                  minHeight="32px"
                >
                  <Typography>{spendsStrings.spends}</Typography>
                  <SpendsRefreshButton onRefresh={handleRefresh} />
                </Grid>

                <Grid xs={12} pb={0.5} pl={0}>
                  <Stack direction="row" justifyContent="center">
                    <IconButton
                      className="goto-page__button"
                      onClick={() => handleChangeYear(-1)}
                      aria-label={spendsStrings.changeYear}
                      sx={{ position: "relative", left: "38px" }}
                    >
                      <ChevronLeft sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Chip
                      variant="outlined"
                      size="large"
                      label={year}
                      sx={{ width: 150 }}
                    />
                    <IconButton
                      className="goto-page__button"
                      onClick={() => handleChangeYear(1)}
                      disabled={year >= currentYear}
                      aria-label={spendsStrings.changeYear}
                      sx={{ position: "relative", right: "38px" }}
                    >
                      <ChevronRight sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Stack>
                </Grid>

                <Grid xs={12} paddingY={1} pl={0}>
                  <Stack direction="row" spacing={1}>
                    {monthsList.map((month, index) => (
                      <Chip
                        key={month}
                        size="large"
                        color={getSpendStatus(index)}
                        label={month}
                        selected={selectedMonthId === index + 1}
                        onClick={() => handleSelectMonth(index + 1)}
                        sx={{
                          flexGrow: 1,
                          "&.MuiChip-colorWarning": {
                            backgroundColor: "warning.light",
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
              </Paper>
            </Grid>

            {/* Middle section */}
            <Grid xs={12} pt={2}>
              <Grid
                container
                direction="row"
                columnSpacing={2}
                rowSpacing={0}
                columns={12}
                alignItems="center"
              >
                <Grid xs={12} md={6}>
                  <SpendsTotalCards totals={totals} />
                </Grid>

                <Grid xs={12} md={6}>
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <SplitButton
                      label={spendsStrings.compute}
                      onClick={handleCompute}
                      menuItems={buildButtonMenu()}
                      isDisabled={disableButtons()}
                    />
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<SendOutlined />}
                      onClick={handleTransfer}
                      disabled={disableButtons() || disableTransfer()}
                    >
                      {spendsStrings.transfer}
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>

            {/* Bottom section */}
            <Grid xs={12} pt={2} flexGrow={1}>
              <Grid
                container
                direction="row"
                columnSpacing={2}
                rowSpacing={0}
                columns={12}
                height="100%"
              >
                {/* Left panel */}
                <Grid xs={12} md={6}>
                  <Grid container direction="column" columns={12} height="100%">
                    <Grid xs={12} flexGrow={1}>
                      <Paper
                        sx={{
                          height: "100%",
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2,
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "#F6EDFF",
                            px: 3,
                            py: 1,
                          }}
                        >
                          <Typography component="h2">
                            {spendsStrings.fileName}
                          </Typography>
                        </Box>

                        <Box sx={{ p: "0.5rem" }}>
                          {selectedMonth?.fileName && (
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                            >
                              <Stack direction="row" alignItems="center">
                                <DescriptionOutlined
                                  color="action"
                                  sx={{ mr: 1 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {selectedMonth.fileName}
                                </Typography>
                              </Stack>

                              <IconButton
                                size="small"
                                onClick={handleDownload}
                                title={spendsStrings.downloadFile}
                              >
                                <FileDownloadOutlined color="action" />
                              </IconButton>
                            </Stack>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Right panel */}
                <Grid xs={12} md={6}>
                  <Grid container direction="column" columns={12} height="100%">
                    <Grid xs={12} flexGrow="1">
                      <Paper
                        sx={{
                          height: "100%",
                          position: "relative",
                          overflow: "hidden",
                          borderRadius: 2,
                          fontFamily: "monospace",
                        }}
                      >
                        <Box
                          sx={{
                            bgcolor: "#F6EDFF",
                            px: 3,
                            py: 1,
                          }}
                        >
                          <Typography component="h2">
                            {spendsStrings.processMessages}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                            p: "0.5rem",
                          }}
                        >
                          {progressMessages.map((message, index) => (
                            <Typography
                              key={message.progressMessageId}
                              sx={{
                                fontFamily: "monospace",
                                fontSize: "0.9rem",
                                color: `${
                                  E_RETAIL_SPEND_MESSAGE_TYPES[
                                    message.spendMessageTypeId
                                  ]
                                }.light`,
                              }}
                            >
                              <span style={{ opacity: 0.5 }}>{index + 1}</span>
                              {` > [${dayjs(message.addedDate)
                                .locale(language)
                                .format("L LT")}] ${message.messageText}`}
                            </Typography>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Dialog
        open={dialogConfig.isOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title"></DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {spendsStrings.actionConfirmation}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={dialogConfig.onConfirm}>Yes</Button>
          <Button onClick={handleCloseDialog} autoFocus>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </DefaultLayout>
  );
};
